import type {ForwardRefExoticComponent, RefAttributes} from 'react'

import {
  Alert as ChakraAlert,
  AlertDescription as ChakraAlertDescription,
  AlertDialog as ChakraAlertDialog,
  AlertDialogBody as ChakraAlertDialogBody,
  AlertDialogContent as ChakraAlertDialogContent,
  AlertDialogFooter as ChakraAlertDialogFooter,
  AlertDialogHeader as ChakraAlertDialogHeader,
  AlertDialogOverlay as ChakraAlertDialogOverlay,
  AlertIcon as ChakraAlertIcon,
  AlertTitle as ChakraAlertTitle,
  AspectRatio as ChakraAspectRatio,
  Badge as ChakraBadge,
  Box as ChakraBox,
  Button as ChakraButton,
  Checkbox as ChakraCheckbox,
  CloseButton as ChakraCloseButton,
  Collapse as ChakraCollapse,
  Divider as ChakraDivider,
  Drawer as ChakraDrawer,
  DrawerBody as ChakraDrawerBody,
  DrawerCloseButton as ChakraDrawerCloseButton,
  DrawerContent as ChakraDrawerContent,
  DrawerFooter as ChakraDrawerFooter,
  DrawerHeader as ChakraDrawerHeader,
  DrawerOverlay as ChakraDrawerOverlay,
  Fade as ChakraFade,
  Flex as ChakraFlex,
  FormControl as ChakraFormControl,
  FormLabel as ChakraFormLabel,
  Heading as ChakraHeading,
  Icon as ChakraIcon,
  IconButton as ChakraIconButton,
  Image as ChakraImage,
  Input as ChakraInput,
  InputGroup as ChakraInputGroup,
  InputLeftAddon as ChakraInputLeftAddon,
  InputRightAddon as ChakraInputRightAddon,
  InputRightElement as ChakraInputRightElement,
  Link as ChakraLink,
  List as ChakraList,
  ListItem as ChakraListItem,
  Menu as ChakraMenu,
  MenuButton as ChakraMenuButton,
  MenuDivider as ChakraMenuDivider,
  MenuItem as ChakraMenuItem,
  MenuList as ChakraMenuList,
  Modal as ChakraModal,
  ModalBody as ChakraModalBody,
  ModalCloseButton as ChakraModalCloseButton,
  ModalContent as ChakraModalContent,
  ModalFooter as ChakraModalFooter,
  ModalHeader as ChakraModalHeader,
  ModalOverlay as ChakraModalOverlay,
  Radio as ChakraRadio,
  RadioGroup as ChakraRadioGroup,
  Select as ChakraSelect,
  SimpleGrid as ChakraSimpleGrid,
  Skeleton as ChakraSkeleton,
  Spinner as ChakraSpinner,
  Stack as ChakraStack,
  Switch as ChakraSwitch,
  Tab as ChakraTab,
  TabList as ChakraTabList,
  TabPanel as ChakraTabPanel,
  TabPanels as ChakraTabPanels,
  Table as ChakraTable,
  Tabs as ChakraTabs,
  Tag as ChakraTag,
  Tbody as ChakraTbody,
  Td as ChakraTd,
  Text as ChakraText,
  Textarea as ChakraTextarea,
  Tooltip as ChakraTooltip,
  Th as ChakraTh,
  Thead as ChakraThead,
  Tr as ChakraTr,
} from '@chakra-ui/react'
import type {
  AlertDescriptionProps,
  AlertDialogProps,
  AlertIconProps,
  AlertProps,
  AlertTitleProps,
  AspectRatioProps,
  BadgeProps,
  BoxProps,
  ButtonProps,
  CheckboxProps,
  CloseButtonProps,
  CollapseProps,
  DividerProps,
  DrawerContentProps,
  DrawerProps,
  FadeProps,
  FlexProps,
  FormControlProps,
  FormLabelProps,
  HeadingProps,
  IconButtonProps,
  IconProps,
  ImageProps,
  InputAddonProps,
  InputElementProps,
  InputGroupProps,
  InputProps,
  LinkProps,
  ListItemProps,
  ListProps,
  MenuButtonProps,
  MenuDividerProps,
  MenuItemProps,
  MenuListProps,
  MenuProps,
  ModalBodyProps,
  ModalContentProps,
  ModalFooterProps,
  ModalHeaderProps,
  ModalOverlayProps,
  ModalProps,
  RadioGroupProps,
  RadioProps,
  SelectProps,
  SimpleGridProps,
  SkeletonProps,
  SpinnerProps,
  StackProps,
  SwitchProps,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableHeadProps,
  TableProps,
  TabListProps,
  TabPanelProps,
  TabPanelsProps,
  TabProps,
  TabsProps,
  TagProps,
  TableRowProps,
  TextProps,
  TextareaProps,
  TooltipProps,
} from '@chakra-ui/react'

/**
 * Chakra UI v1 types every component as `ComponentWithAs<...>` — a call
 * signature generic over the `as` prop, constrained to React's `ElementType`
 * (every intrinsic element plus every component type). TypeScript 5 expands
 * that constraint past its union-size limit and reports `TS2590: Expression
 * produces a union type that is too complex to represent`, both at
 * `React.ComponentProps<typeof X>` and at plain JSX instantiation. TypeScript
 * 4.9 tolerated it; 5.x does not.
 *
 * Nothing in this app passes `as` to these components, so re-declare them here
 * as ordinary ref-forwarding components carrying their concrete prop types.
 * This is a type-level cast only — the runtime components are untouched, and
 * every prop keeps its real type.
 *
 * Import from here instead of `@chakra-ui/react` at any site that trips
 * TS2590. Delete this module with the Chakra v2 upgrade (AB#19738), which
 * retypes these components in a form TypeScript 5 can represent.
 */
type Monomorphic<Props, Element> = ForwardRefExoticComponent<
  Props & RefAttributes<Element>
>

export const Alert = ChakraAlert as Monomorphic<AlertProps, HTMLDivElement>

export const AlertDescription = ChakraAlertDescription as Monomorphic<
  AlertDescriptionProps,
  HTMLDivElement
>

export const AlertIcon = ChakraAlertIcon as Monomorphic<
  AlertIconProps,
  SVGSVGElement
>

export const Button = ChakraButton as Monomorphic<
  ButtonProps,
  HTMLButtonElement
>

export const Checkbox = ChakraCheckbox as Monomorphic<
  CheckboxProps,
  HTMLInputElement
>

export const CloseButton = ChakraCloseButton as Monomorphic<
  CloseButtonProps,
  HTMLButtonElement
>

export const Flex = ChakraFlex as Monomorphic<FlexProps, HTMLDivElement>

export const FormControl = ChakraFormControl as Monomorphic<
  FormControlProps,
  HTMLDivElement
>

export const FormLabel = ChakraFormLabel as Monomorphic<
  FormLabelProps,
  HTMLLabelElement
>

export const Icon = ChakraIcon as Monomorphic<IconProps, SVGSVGElement>

export const IconButton = ChakraIconButton as Monomorphic<
  IconButtonProps,
  HTMLButtonElement
>

export const Input = ChakraInput as Monomorphic<InputProps, HTMLInputElement>

export const Radio = ChakraRadio as Monomorphic<RadioProps, HTMLInputElement>

export const Select = ChakraSelect as Monomorphic<
  SelectProps,
  HTMLSelectElement
>

export const Text = ChakraText as Monomorphic<TextProps, HTMLParagraphElement>

export const Textarea = ChakraTextarea as Monomorphic<
  TextareaProps,
  HTMLTextAreaElement
>

export const Box = ChakraBox as Monomorphic<BoxProps, HTMLElement>

export const Stack = ChakraStack as Monomorphic<StackProps, HTMLElement>

export const Collapse = ChakraCollapse as Monomorphic<
  CollapseProps,
  HTMLElement
>

export const RadioGroup = ChakraRadioGroup as Monomorphic<
  RadioGroupProps,
  HTMLElement
>

export const Skeleton = ChakraSkeleton as Monomorphic<
  SkeletonProps,
  HTMLElement
>

export const Heading = ChakraHeading as Monomorphic<
  HeadingProps,
  HTMLHeadingElement
>

export const ModalOverlay = ChakraModalOverlay as Monomorphic<
  ModalOverlayProps,
  HTMLElement
>

export const ModalContent = ChakraModalContent as Monomorphic<
  ModalContentProps,
  HTMLElement
>

export const ModalHeader = ChakraModalHeader as Monomorphic<
  ModalHeaderProps,
  HTMLElement
>

export const ModalFooter = ChakraModalFooter as Monomorphic<
  ModalFooterProps,
  HTMLElement
>

export const ModalBody = ChakraModalBody as Monomorphic<
  ModalBodyProps,
  HTMLElement
>

export const ModalCloseButton = ChakraModalCloseButton as Monomorphic<
  CloseButtonProps,
  HTMLElement
>

export const InputGroup = ChakraInputGroup as Monomorphic<
  InputGroupProps,
  HTMLElement
>

export const InputLeftAddon = ChakraInputLeftAddon as Monomorphic<
  InputAddonProps,
  HTMLElement
>

export const InputRightElement = ChakraInputRightElement as Monomorphic<
  InputElementProps,
  HTMLElement
>

export const Switch = ChakraSwitch as Monomorphic<SwitchProps, HTMLInputElement>

export const Image = ChakraImage as Monomorphic<ImageProps, HTMLImageElement>

export const Modal = ChakraModal as Monomorphic<ModalProps, HTMLElement>

export const Spinner = ChakraSpinner as Monomorphic<SpinnerProps, HTMLElement>

export const Table = ChakraTable as Monomorphic<TableProps, HTMLTableElement>

export const Tbody = ChakraTbody as Monomorphic<TableBodyProps, HTMLElement>

export const Td = ChakraTd as Monomorphic<TableCellProps, HTMLElement>

export const Th = ChakraTh as Monomorphic<TableColumnHeaderProps, HTMLElement>

export const Thead = ChakraThead as Monomorphic<TableHeadProps, HTMLElement>

export const Tr = ChakraTr as Monomorphic<TableRowProps, HTMLElement>

export const InputRightAddon = ChakraInputRightAddon as Monomorphic<
  InputAddonProps,
  HTMLElement
>

export const Menu = ChakraMenu as Monomorphic<MenuProps, HTMLElement>

export const MenuButton = ChakraMenuButton as Monomorphic<
  MenuButtonProps,
  HTMLElement
>

export const MenuList = ChakraMenuList as Monomorphic<
  MenuListProps,
  HTMLElement
>

export const MenuItem = ChakraMenuItem as Monomorphic<
  MenuItemProps,
  HTMLElement
>

export const List = ChakraList as Monomorphic<ListProps, HTMLUListElement>

export const ListItem = ChakraListItem as Monomorphic<
  ListItemProps,
  HTMLLIElement
>

export const Fade = ChakraFade as Monomorphic<FadeProps, HTMLElement>

export const MenuDivider = ChakraMenuDivider as Monomorphic<
  MenuDividerProps,
  HTMLElement
>

export const Badge = ChakraBadge as Monomorphic<BadgeProps, HTMLElement>

export const AlertTitle = ChakraAlertTitle as Monomorphic<
  AlertTitleProps,
  HTMLElement
>

export const AlertDialog = ChakraAlertDialog as Monomorphic<
  AlertDialogProps,
  HTMLElement
>

export const AlertDialogBody = ChakraAlertDialogBody as Monomorphic<
  ModalBodyProps,
  HTMLDivElement
>

export const AlertDialogContent = ChakraAlertDialogContent as Monomorphic<
  ModalContentProps,
  HTMLElement
>

export const AlertDialogFooter = ChakraAlertDialogFooter as Monomorphic<
  ModalFooterProps,
  HTMLDivElement
>

export const AlertDialogHeader = ChakraAlertDialogHeader as Monomorphic<
  ModalHeaderProps,
  HTMLElement
>

export const AlertDialogOverlay = ChakraAlertDialogOverlay as Monomorphic<
  ModalOverlayProps,
  HTMLDivElement
>

export const Link = ChakraLink as Monomorphic<LinkProps, HTMLAnchorElement>

export const Drawer = ChakraDrawer as Monomorphic<DrawerProps, HTMLElement>

export const DrawerBody = ChakraDrawerBody as Monomorphic<
  ModalBodyProps,
  HTMLElement
>

export const DrawerFooter = ChakraDrawerFooter as Monomorphic<
  ModalFooterProps,
  HTMLElement
>

export const DrawerHeader = ChakraDrawerHeader as Monomorphic<
  ModalHeaderProps,
  HTMLElement
>

export const DrawerOverlay = ChakraDrawerOverlay as Monomorphic<
  ModalOverlayProps,
  HTMLElement
>

export const DrawerContent = ChakraDrawerContent as Monomorphic<
  DrawerContentProps,
  HTMLElement
>

export const DrawerCloseButton = ChakraDrawerCloseButton as Monomorphic<
  CloseButtonProps,
  HTMLButtonElement
>

export const AspectRatio = ChakraAspectRatio as Monomorphic<
  AspectRatioProps,
  HTMLElement
>

export const SimpleGrid = ChakraSimpleGrid as Monomorphic<
  SimpleGridProps,
  HTMLElement
>

export const Divider = ChakraDivider as Monomorphic<DividerProps, HTMLElement>

export const Tag = ChakraTag as Monomorphic<TagProps, HTMLSpanElement>

export const Tooltip = ChakraTooltip as Monomorphic<
  TooltipProps,
  HTMLDivElement
>

export const Tabs = ChakraTabs as Monomorphic<TabsProps, HTMLDivElement>

export const TabList = ChakraTabList as Monomorphic<
  TabListProps,
  HTMLDivElement
>

export const TabPanels = ChakraTabPanels as Monomorphic<
  TabPanelsProps,
  HTMLDivElement
>

export const TabPanel = ChakraTabPanel as Monomorphic<
  TabPanelProps,
  HTMLDivElement
>

export const Tab = ChakraTab as Monomorphic<TabProps, HTMLButtonElement>
